import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Target, Sparkles } from 'lucide-react';

const HeroOther = ({ item }) => {
  return (
    <section 
      className="relative w-full overflow-hidden bg-[#F6F4EF] flex items-center"
      style={{ paddingTop: '140px', paddingBottom: '80px', minHeight: '60vh' }}
    >
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Decorative Background Circles */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.06, scale: 1 }}
        transition={{ duration: 1.2 }}
        className="absolute top-[10%] right-[8%] w-32 h-32 rounded-full bg-primary blur-2xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.08, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.2 }}
        className="absolute bottom-[15%] left-[5%] w-24 h-24 rounded-full bg-yellow-400 blur-2xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.4 }}
        className="absolute top-[50%] right-[15%] w-16 h-16 rounded-full bg-red-500 blur-2xl"
      />

      {/* Ambient Background Effects */}
      <motion.div
        animate={{
          y: [0, -20, 0],
          opacity: [0.03, 0.05, 0.03]
        }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary blur-3xl"
      />
      <motion.div
        animate={{
          y: [0, 20, 0],
          opacity: [0.03, 0.05, 0.03]
        }}
        transition={{ duration: 7, repeat: Infinity }}
        className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-yellow-400 blur-3xl"
      />

      {/* Main Container */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 w-full relative z-10">
        <div className="grid lg:grid-cols-[45%_55%] gap-12 lg:gap-16 items-center">
          
          {/* LEFT SIDE - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Service Tag */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block"
            >
              <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
                Professional Service
              </span>
            </motion.div>

            {/* Main Title with Underline */}
            <div className="relative">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold text-gray-900 !leading-[1.3] inline-block relative"
                style={{ letterSpacing: '-0.02em' }}
              >
                {item.h1}
                {/* Animated Underline */}
                {/* <svg
                  className="absolute -bottom-3 left-0 w-full h-3"
                  viewBox="0 0 200 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M2 8C50 2, 150 2, 198 8"
                    stroke="#1b7840"
                    strokeWidth="4"
                    strokeLinecap="round"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 0.6 }}
                  />
                </svg> */}
              </motion.h1>
            </div>

            {/* Subtitle with Line */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex items-center gap-6 mt-10"
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '60px' }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="h-1 bg-primary rounded-full flex-shrink-0"
              />
              <p className="text-xl md:text-2xl text-gray-600 font-semibold leading-relaxed m-0">
                {item.s1}
              </p>
            </motion.div>
          </motion.div>

          {/* RIGHT SIDE - Description Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <motion.div
              whileHover={{ 
                y: -8,
                transition: { duration: 0.3 }
              }}
              className="relative bg-white p-10 md:p-12 lg:p-14 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 group"
            >
              {/* Gradient overlay on hover */}
              <motion.div
                className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(27, 120, 64, 0.05) 0%, transparent 100%)' 
                }}
              />

              {/* Description Text */}
              <div className="relative z-10">
                <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed">
                  {item.c1}
                  <br /><br />
                  {item.c2}
                </p>
              </div>

              {/* Floating Icon Decorations */}
              <div className="hidden lg:block">
                {/* Icon 1 - Top Right */}
                <motion.div
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 10, 0],
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute top-5 right-5 opacity-10"
                >
                  <TrendingUp className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </motion.div>

                {/* Icon 2 - Bottom Left */}
                <motion.div
                  animate={{
                    y: [0, 10, 0],
                    rotate: [0, -10, 0],
                  }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute bottom-5 left-5 opacity-10"
                >
                  <Target className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </motion.div>
              </div>

              {/* Accent Corner */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-tr-3xl rounded-bl-full"></div>

              {/* Bottom accent line */}
              <motion.div
                className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary via-primary to-transparent rounded-b-3xl"
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 1, delay: 0.8 }}
              />

              {/* Sparkle effect on hover */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                whileHover={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute -top-3 -right-3 w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center"
              >
                <Sparkles className="w-4 h-4 text-primary" />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Decorative Rotating Square */}
      <motion.div
        animate={{
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="hidden lg:block absolute bottom-12 left-12 w-16 h-16 border-4 border-primary/20 z-0"
        style={{ transform: 'rotate(45deg)' }}
      />
    </section>
  );
};

export default HeroOther;